import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import { offersAPI, candidatesAPI } from '../../lib/api';
import { getScoreColor } from '../../lib/scoreUtils';

const ROLE_TYPE_LABELS = {
  technical: 'Technical / IC', sales: 'Sales / Revenue',
  leadership: 'Leadership / Exec', general: 'General',
};
const IMPORTANCE_COLORS = {
  Critical: '#DC2626', Important: '#D97706', Preferred: '#005A80', Desirable: '#64748B',
};

// At-a-glance recruiter verdict (who / relevance / strongest evidence / biggest caveat).
function ExecutiveSummary({ card }) {
  const rows = [
    ['Who', card?.who], ['Why relevant', card?.relevance],
    ['Strongest evidence', card?.strongest_evidence], ['Biggest caveat', card?.biggest_caveat],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <Card data-testid="executive-summary-card" style={{ background: 'linear-gradient(180deg,#F8FAFF,#EEF4FF)', border: '1px solid #DBE4FF', borderRadius: 14, padding: '1.5rem', marginBottom: '1.5rem' }}>
      <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', color: '#1E3A8A' }}>Executive Summary</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {rows.map(([label, val]) => (
          <div key={label}>
            <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#3730A3', marginBottom: '0.25rem' }}>{label}</div>
            <div style={{ fontSize: '0.8125rem', color: '#002A38', lineHeight: 1.5 }}>{val}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// Prominent material-issue banner (empty when there are no genuine red flags).
function RedFlags({ flags }) {
  if (!(flags || []).length) return null;
  return (
    <Card data-testid="red-flags-banner" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#B91C1C' }}>Red flags ({flags.length})</span>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {flags.map((f, i) => (
          <li key={`rf-${i}`} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8125rem', color: '#991B1B' }}>
            <span style={{ flexShrink: 0 }}>•</span>{f}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// JD requirement → candidate match matrix (0-5 dots) with importance + verifiable evidence.
function RequirementMatrix({ rows, grounding }) {
  if (!(rows || []).length) return null;
  const gf = grounding && grounding.with_quote ? grounding : null;
  return (
    <Card data-testid="requirement-matrix" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem', marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, margin: 0 }}>Requirement Match Matrix</h3>
        {gf && (
          <span data-testid="evidence-grounding-summary" title="Evidence quotes checked against the resume text (verbatim match)."
            style={{ fontSize: '0.625rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: 999,
              background: gf.grounded_fraction >= 0.8 ? '#DCFCE7' : gf.grounded_fraction >= 0.5 ? '#FEF3C7' : '#FEE2E2',
              color: gf.grounded_fraction >= 0.8 ? '#15803D' : gf.grounded_fraction >= 0.5 ? '#B45309' : '#B91C1C' }}>
            {gf.verified}/{gf.with_quote} evidence verified in resume
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {rows.map((r, i) => (
          <div key={`req-${i}`} data-testid="requirement-matrix-row" style={{ paddingBottom: '0.75rem', borderBottom: i < rows.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#002A38' }}>{r.requirement}</span>
                {r.importance && (
                  <span style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', color: IMPORTANCE_COLORS[r.importance] || '#64748B', border: `1px solid ${IMPORTANCE_COLORS[r.importance] || '#CBD5E1'}33`, background: `${IMPORTANCE_COLORS[r.importance] || '#64748B'}12`, borderRadius: 999, padding: '0.1rem 0.4rem' }}>{r.importance}</span>
                )}
                {r.evidence_verified === true && (
                  <span data-testid="evidence-verified" title="Quote found verbatim in the resume" style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#15803D', background: '#DCFCE7', borderRadius: 999, padding: '0.1rem 0.4rem' }}>✓ verified</span>
                )}
                {r.evidence_verified === false && (
                  <span data-testid="evidence-unverified" title="AI cited a quote that was NOT found in the resume" style={{ fontSize: '0.5625rem', fontWeight: 700, color: '#B91C1C', background: '#FEE2E2', borderRadius: 999, padding: '0.1rem 0.4rem' }}>⚠ unverified</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 3, alignItems: 'center' }} title={`Match level ${r.match_level ?? 0}/5`}>
                {[0, 1, 2, 3, 4].map((d) => (
                  <span key={d} style={{ width: 9, height: 9, borderRadius: '50%', background: d < (r.match_level ?? 0) ? getScoreColor(((r.match_level ?? 0) / 5) * 100) : '#ECE7DE' }} />
                ))}
              </div>
            </div>
            {r.evidence_quote ? (
              <p style={{ fontSize: '0.6875rem', color: r.evidence_verified === false ? '#B91C1C' : '#334155', marginTop: '0.375rem', paddingLeft: '0.5rem', borderLeft: `2px solid ${r.evidence_verified === false ? '#FCA5A5' : '#86EFAC'}` }}>“{r.evidence_quote}”</p>
            ) : r.evidence ? (
              <p style={{ fontSize: '0.6875rem', color: '#64748B', marginTop: '0.375rem', paddingLeft: '0.5rem', borderLeft: '2px solid #CBD5E1', fontStyle: 'italic' }}>{r.evidence}</p>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

// Hiring-manager "reality check" + the profile of who succeeds in this role.
function RealityAndProfile({ realityCheck, successProfile }) {
  const rc = realityCheck || {};
  const hasRC = rc.could_perform || rc.done_similar_before || rc.hiring_manager_concern;
  if (!hasRC && !successProfile) return null;
  const rcRows = [
    ['Could perform the role?', rc.could_perform],
    ['Done similar before?', rc.done_similar_before],
    ['Biggest thing to probe', rc.hiring_manager_concern],
  ].filter(([, v]) => v);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: hasRC && successProfile ? '1fr 1fr' : '1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
      {hasRC && (
        <Card data-testid="reality-check-card" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Reality Check</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {rcRows.map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#005A80', marginBottom: '0.15rem' }}>{label}</div>
                <div style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.5 }}>{val}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
      {successProfile && (
        <Card data-testid="success-profile-card" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>Success Profile</h3>
          <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.7 }}>{successProfile}</p>
        </Card>
      )}
    </div>
  );
}

// Seniority signals extracted by the engine (team size, P&L, geos, quantified wins).
function SeniorSignals({ extracted }) {
  const e = extracted || {};
  const chips = [];
  if (e.current_title) chips.push(['Current title', e.current_title]);
  if (e.seniority_level) chips.push(['Seniority', e.seniority_level]);
  if (e.team_size_managed) chips.push(['Team managed', `${e.team_size_managed}`]);
  if (e.budget_or_pnl) chips.push(['Budget / P&L', e.budget_or_pnl]);
  if ((e.geographies || []).length) chips.push(['Geographies', e.geographies.join(', ')]);
  if (e.career_trajectory) chips.push(['Trajectory', e.career_trajectory]);
  const outcomes = e.quantified_outcomes || [];
  if (!chips.length && !outcomes.length) return null;
  return (
    <Card data-testid="senior-signals-card" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem', marginTop: '1.5rem' }}>
      <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Seniority Signals</h3>
      {chips.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: outcomes.length ? '1rem' : 0 }}>
          {chips.map(([label, val]) => (
            <div key={label}>
              <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', marginBottom: '0.15rem' }}>{label}</div>
              <div style={{ fontSize: '0.8125rem', color: '#002A38' }}>{val}</div>
            </div>
          ))}
        </div>
      )}
      {outcomes.length > 0 && (
        <div>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#059669', marginBottom: '0.375rem' }}>Quantified outcomes</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {outcomes.map((o, i) => (
              <li key={`qo-${i}`} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
                <span style={{ color: '#059669', flexShrink: 0 }}>↑</span>{o}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

const RECENCY_META = {
  current: { label: 'Current', color: '#059669' },
  recent: { label: 'Recent', color: '#0076A3' },
  dated: { label: 'Dated', color: '#D97706' },
  stale: { label: 'Stale', color: '#DC2626' },
};

// Skill-integrity signals: anti-gaming (listed-but-not-demonstrated) + recency of required skills.
function SkillIntegrity({ evidence, recency }) {
  const unsub = (evidence && evidence.unsubstantiated_skills) || [];
  const buckets = (recency && recency.buckets) || {};
  const considered = (recency && recency.considered) || 0;
  const staleSkills = ((recency && recency.details) || []).filter((d) => d.recency === 'stale');
  if (!unsub.length && considered === 0) return null;
  return (
    <Card data-testid="skill-integrity-card" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem', marginTop: '1.5rem' }}>
      <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Skill Evidence &amp; Recency</h3>

      {unsub.length > 0 && (
        <div data-testid="unsubstantiated-skills" style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '0.875rem 1rem', marginBottom: considered ? '1.25rem' : 0 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#B45309', marginBottom: '0.5rem' }}>
            Listed but not demonstrated in work history ({unsub.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {unsub.map((s) => (
              <span key={s} style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#FFEDD5', color: '#9A3412' }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {considered > 0 && (
        <div>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', marginBottom: '0.625rem' }}>
            Required-skill recency ({considered} dated)
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: staleSkills.length ? '0.875rem' : 0 }}>
            {['current', 'recent', 'dated', 'stale'].map((k) => (
              (buckets[k] || 0) > 0 ? (
                <span key={k} data-testid={`recency-${k}`} style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 999, background: `${RECENCY_META[k].color}14`, color: RECENCY_META[k].color }}>
                  {RECENCY_META[k].label}: {buckets[k]}
                </span>
              ) : null
            ))}
          </div>
          {staleSkills.length > 0 && (
            <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>
              Stale (&gt;7y): {staleSkills.map((d) => `${d.skill} (${d.last_used_year})`).join(', ')}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// High-accuracy transparency: which models scored + what the LLM-judge changed.
function EnsembleCritique({ ensembleModels, critique }) {
  const models = ensembleModels || [];
  const c = critique || {};
  const applied = c.adjustments_applied || [];
  const unsupported = c.unsupported_claims || [];
  if (!models.length && !c.summary && !applied.length && !unsupported.length) return null;
  const counts = models.reduce((acc, m) => { acc[m] = (acc[m] || 0) + 1; return acc; }, {});
  return (
    <Card data-testid="ensemble-critique-card" style={{ background: 'linear-gradient(180deg,#F5F3FF,#FaF9FF)', border: '1px solid #DDD6FE', borderRadius: 14, padding: '1.5rem', marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, margin: 0, color: '#5B21B6' }}>AI Ensemble &amp; Critique</h3>
        <span style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#F05000', background: '#EDE9FE', borderRadius: 999, padding: '0.1rem 0.45rem' }}>high-accuracy</span>
      </div>
      {models.length > 0 && (
        <div style={{ marginBottom: c.summary || applied.length ? '1rem' : 0 }}>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#F2600C', marginBottom: '0.375rem' }}>Scored by {models.length} model runs</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {Object.entries(counts).map(([m, n]) => (
              <span key={m} style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#fff', border: '1px solid #DDD6FE', color: '#5B21B6' }}>{m}{n > 1 ? ` ×${n}` : ''}</span>
            ))}
          </div>
        </div>
      )}
      {c.summary && (
        <p style={{ fontSize: '0.8125rem', color: '#4C1D95', lineHeight: 1.6, marginBottom: applied.length || unsupported.length ? '1rem' : 0 }}>
          <span style={{ fontWeight: 700 }}>Reviewer audit: </span>{c.summary}
        </p>
      )}
      {applied.length > 0 && (
        <div style={{ marginBottom: unsupported.length ? '1rem' : 0 }}>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#F2600C', marginBottom: '0.375rem' }}>Reviewer adjusted {applied.length} score(s)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {applied.map((a, i) => (
              <div key={`adj-${i}`} data-testid="critique-adjustment" style={{ fontSize: '0.75rem', color: '#334155' }}>
                <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{(a.dimension || '').replace(/_/g, ' ')}</span>
                {': '}
                <span style={{ color: '#94A3B8' }}>{a.from}</span>
                <span style={{ color: a.to < a.from ? '#DC2626' : '#059669', fontWeight: 700 }}> → {a.to}</span>
                {a.reason ? <span style={{ color: '#64748B' }}> · {a.reason}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}
      {unsupported.length > 0 && (
        <div>
          <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#B91C1C', marginBottom: '0.375rem' }}>Unsupported claims flagged</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {unsupported.map((u, i) => (
              <li key={`uc-${i}`} style={{ fontSize: '0.75rem', color: '#7F1D1D', display: 'flex', gap: '0.4rem' }}><span>•</span>{u}</li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}


// Recruiter feedback loop: agree/disagree with the AI match + optional correction/note.
function MatchFeedback({ candidateId, match, existing, loadCandidate }) {
  const [note, setNote] = useState(existing?.note || '');
  const [correctedFit, setCorrectedFit] = useState(existing?.corrected_fit || '');
  const [saving, setSaving] = useState(false);

  const submit = async (agree) => {
    setSaving(true);
    try {
      await candidatesAPI.matchFeedback(candidateId, { agree, corrected_fit: correctedFit || null, note });
      toast.success('Feedback recorded — thanks, this helps calibrate scoring');
      loadCandidate && loadCandidate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save feedback');
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="match-feedback" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem', marginTop: '1.5rem' }}>
      <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Was this AI assessment accurate?</h3>
      <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.875rem' }}>
        Your feedback helps us calibrate the fitment engine. The AI scored this candidate <strong>{match.overall_score}</strong> ({match.fit_recommendation}).
      </p>
      {existing && (
        <div data-testid="feedback-existing" style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: 8, marginBottom: '0.875rem',
          background: existing.agree ? '#DCFCE7' : '#FEF3C7', color: existing.agree ? '#15803D' : '#B45309' }}>
          Last feedback: {existing.agree ? 'Agreed' : 'Disagreed'} by {existing.by}
          {existing.corrected_fit ? ` · suggested "${existing.corrected_fit}"` : ''}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>Corrected fit (optional)</label>
          <select data-testid="feedback-corrected-fit" value={correctedFit} onChange={(e) => setCorrectedFit(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FAF7F2', fontSize: '0.8125rem' }}>
            <option value="">— No change —</option>
            <option value="Exceptional">Exceptional</option>
            <option value="Strong">Strong</option>
            <option value="Good">Good</option>
            <option value="Maybe">Maybe</option>
            <option value="Reject">Reject</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>Note (optional)</label>
          <input data-testid="feedback-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What did the AI get wrong / right?"
            style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FAF7F2', fontSize: '0.8125rem', boxSizing: 'border-box' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button data-testid="feedback-agree-btn" disabled={saving} onClick={() => submit(true)}
          style={{ background: '#10B981', color: '#fff', borderRadius: 10, fontWeight: 600 }}>👍 Agree</Button>
        <Button data-testid="feedback-disagree-btn" disabled={saving} variant="outline" onClick={() => submit(false)}
          style={{ borderRadius: 10, fontWeight: 600, borderColor: '#F59E0B', color: '#B45309' }}>👎 Disagree</Button>
      </div>
    </Card>
  );
}

export default function CandidateProfileTabs({ candidate, candidateId, parsed, match, scoreBreakdown, matchedSkillsSet, depthAnalysis, analyzingDepth, runDepthAnalysis, offerProb, pipelineHistory, offer, showOfferForm, setShowOfferForm, offerForm, setOfferForm, onboardingPlan, setOnboardingPlan, loadCandidate }) {
  return (
<>
        <TabsContent value="overview">
          <ExecutiveSummary card={match.summary_card} />
          <RedFlags flags={match.red_flags} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {/* Score Breakdown */}
            <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
              <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>Score Breakdown</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }} data-testid="match-meta">
                {match.role_type && (
                  <span data-testid="role-type-badge" title="Role type auto-detected from the JD — selects the dimension weight preset."
                    style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#EDE9FE', color: '#F05000' }}>
                    {ROLE_TYPE_LABELS[match.role_type] || match.role_type}
                  </span>
                )}
                {match.needs_review && (
                  <span data-testid="needs-review-flag" title={(match.review_reasons || []).join('; ')}
                    style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#FEF3C7', color: '#B45309', cursor: 'help' }}>
                    ⚠ Needs manual review
                  </span>
                )}
                {match.bias_blind && (
                  <span data-testid="bias-blind-flag" title="Scored with personal identifiers redacted"
                    style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#F3E8FF', color: '#7E22CE' }}>
                    Bias-blind scored
                  </span>
                )}
                {(match.scoring_version === 'phase3-v1' || match.scoring_version === 'phase4-v1' || match.semantic_matching) && (
                  <span data-testid="high-accuracy-flag"
                    title={`Semantic/transferable-skill matching${match.self_consistency_runs ? ` · averaged over ${match.self_consistency_runs} AI passes` : ''}`}
                    style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#E0F2FE', color: '#005A80', cursor: 'help' }}>
                    High-accuracy
                  </span>
                )}
                {match.semantic_fit != null && (
                  <span data-testid="semantic-fit-flag"
                    title="Document-level semantic match between this résumé and the job description (local embeddings). Blended 10% into the high-accuracy score."
                    style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#ECFEFF', color: '#0E7490', cursor: 'help' }}>
                    Semantic fit {Math.round(match.semantic_fit)}%
                  </span>
                )}
                {match.confidence && (
                  <span style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, textTransform: 'capitalize',
                    background: match.confidence === 'high' ? '#DCFCE7' : match.confidence === 'medium' ? '#FEF3C7' : '#FEE2E2',
                    color: match.confidence === 'high' ? '#15803D' : match.confidence === 'medium' ? '#B45309' : '#B91C1C' }}>
                    {match.confidence} confidence
                  </span>
                )}
                {match.title_relevance && (
                  <span data-testid="title-relevance-badge" title={match.title_relevance.note}
                    style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#FFF7ED', color: '#C2410C', cursor: 'help' }}>
                    Title relevance {match.title_relevance.score}%
                  </span>
                )}
                {match.must_have_coverage != null && (
                  <span style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#EEF2FF', color: '#3730A3' }}>
                    Must-have coverage {Math.round(match.must_have_coverage * 100)}%
                  </span>
                )}
                {match.weights_used && (
                  <span title={`Weights: ${Object.entries(match.weights_used).filter(([, w]) => w > 0).sort((a, b) => b[1] - a[1]).map(([k, w]) => `${k.replace(/_/g, ' ')} ${Math.round(w * 100)}%`).join(' · ')}`}
                    style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', cursor: 'help', textDecoration: 'underline dotted' }}>
                    weighted score
                  </span>
                )}
              </div>
              {(match.must_have_gaps || []).length > 0 && (
                <div data-testid="must-have-gaps" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '0.625rem 0.75rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#B91C1C' }}>Missing must-have skills: </span>
                  <span style={{ fontSize: '0.75rem', color: '#B91C1C' }}>{match.must_have_gaps.join(', ')}</span>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {Object.entries(scoreBreakdown).map(([key, val]) => (
                  <div key={key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 500, textTransform: 'capitalize' }}>
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span style={{ fontSize: '0.8125rem', fontFamily: 'JetBrains Mono', color: getScoreColor(val?.score || 0) }}>
                        {val?.score || 0}/100
                      </span>
                    </div>
                    <div className="score-progress" data-testid="score-breakdown-progress">
                      <div className="score-progress-fill" style={{ width: `${val?.score || 0}%`, background: getScoreColor(val?.score || 0) }} />
                    </div>
                    <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>{val?.details}</p>
                    {val?.evidence && (
                      <p style={{ fontSize: '0.6875rem', color: '#64748B', marginTop: '0.25rem', paddingLeft: '0.5rem', borderLeft: '2px solid #CBD5E1', fontStyle: 'italic' }}>
                        “{val.evidence}”
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* Strengths & Concerns */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
                <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#10B981' }}>Strengths</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(match.strengths || []).map((s, i) => (
                    <li key={`strength-${i}`} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
                      <span style={{ color: '#10B981', flexShrink: 0 }}>+</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </Card>
              <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
                <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#F59E0B' }}>Concerns</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(match.concerns || []).map((c, i) => (
                    <li key={`concern-${i}`} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))' }}>
                      <span style={{ color: '#F59E0B', flexShrink: 0 }}>!</span>
                      {c}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>

          {/* Executive fitment detail */}
          <RequirementMatrix rows={match.requirement_matrix} grounding={match.evidence_grounding} />
          <RealityAndProfile realityCheck={match.reality_check} successProfile={match.success_profile} />
          <SeniorSignals extracted={match.extracted} />
          <SkillIntegrity evidence={match.skill_evidence} recency={match.skill_recency} />
          <EnsembleCritique ensembleModels={match.ensemble_models} critique={match.critique} />

          {/* Skills Match */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
            <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
              <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Matched Skills</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                {(match.matched_skills || []).map((skill) => (
                  <span key={skill} style={{ padding: '0.25rem 0.625rem', borderRadius: 999, background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', fontSize: '0.75rem', color: '#059669' }}>{skill}</span>
                ))}
              </div>
            </Card>
            <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
              <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Missing Skills</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                {(match.missing_skills || []).map((skill) => (
                  <span key={skill} style={{ padding: '0.25rem 0.625rem', borderRadius: 999, background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', fontSize: '0.75rem', color: '#DC2626' }}>{skill}</span>
                ))}
              </div>
            </Card>
          </div>

          {/* Transferable / adjacent skills (Phase 3 semantic matching) */}
          {(match.transferable_matched || []).length > 0 && (
            <Card data-testid="transferable-skills" style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem', marginTop: '1.5rem' }}>
              <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>Transferable Skills</h3>
              <p style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.875rem' }}>
                Required skills the candidate can likely cover via closely related experience (credited as partial coverage).
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {match.transferable_matched.map((t, i) => (
                  <div key={`trans-${i}`} style={{ fontSize: '0.8125rem' }}>
                    <span style={{ fontWeight: 600, color: '#005A80' }}>{t.required || t}</span>
                    {t.via && <span style={{ color: 'hsl(var(--muted-foreground))' }}> — via {t.via}</span>}
                    {t.evidence && <p style={{ fontSize: '0.6875rem', color: '#64748B', marginTop: '0.125rem', paddingLeft: '0.5rem', borderLeft: '2px solid #BAE6FD', fontStyle: 'italic' }}>“{t.evidence}”</p>}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* AI Summary */}
          {match.summary && (
            <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem', marginTop: '1.5rem' }}>
              <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>AI Summary</h3>
              <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.7 }}>{match.summary}</p>
            </Card>
          )}

          {(match.overall_score != null) && (
            <MatchFeedback candidateId={candidateId} match={match} existing={candidate?.match_feedback} loadCandidate={loadCandidate} />
          )}
        </TabsContent>

        <TabsContent value="skills">
          <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1.5rem' }}>Extracted Skills</h3>
            {Object.entries(parsed.skills || {}).map(([category, skills]) => (
              skills && skills.length > 0 && (
                <div key={category} style={{ marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                    {category.replace(/_/g, ' ')}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                    {skills.map((skill) => (
                      <span key={skill} style={{
                        padding: '0.25rem 0.625rem', borderRadius: 999,
                        background: matchedSkillsSet.has(skill) ? 'rgba(16, 185, 129, 0.15)' : 'rgba(240, 80, 0, 0.1)',
                        border: `1px solid ${matchedSkillsSet.has(skill) ? 'rgba(16, 185, 129, 0.3)' : 'rgba(240, 80, 0, 0.2)'}`,
                        fontSize: '0.75rem',
                        color: matchedSkillsSet.has(skill) ? '#059669' : '#0076A3'
                      }}>{skill}</span>
                    ))}
                  </div>
                </div>
              )
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="experience">
          <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1.5rem' }}>Work Experience</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {(parsed.experience || []).map((exp, i) => (
                <div key={`exp-${exp.company}-${i}`} style={{ paddingLeft: '1.25rem', borderLeft: '2px solid hsl(var(--border))' }}>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{exp.title}</div>
                  <div style={{ fontSize: '0.8125rem', color: '#0076A3', marginBottom: '0.375rem' }}>{exp.company} &middot; {exp.duration}</div>
                  <ul style={{ listStyle: 'disc', paddingLeft: '1rem', margin: 0 }}>
                    {(exp.highlights || []).map((h, j) => (
                      <li key={`hl-${i}-${j}`} style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>{h}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="education">
          <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
            <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1.5rem' }}>Education</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {(parsed.education || []).map((edu, i) => (
                <div key={`edu-${edu.institution}-${i}`} style={{ paddingLeft: '1.25rem', borderLeft: '2px solid hsl(var(--border))' }}>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{edu.degree}</div>
                  <div style={{ fontSize: '0.8125rem', color: '#0076A3' }}>{edu.institution} &middot; {edu.year}</div>
                </div>
              ))}
              {(parsed.certifications || []).length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Certifications</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                    {parsed.certifications.map((cert, i) => (
                      <span key={`cert-${cert}`} style={{ padding: '0.25rem 0.625rem', borderRadius: 999, background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', fontSize: '0.75rem', color: '#D97706' }}>{cert}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </>
  );
}